// UserProvider
import { SK } from '@/constants';
import { isValidJSON } from '@/utilities/checker';
import React, { createContext, useState, useContext, useEffect } from 'react';

// Define the types for user data
interface User {
  id?: string;
  username?: string;
  email?: string;
  token?: string;
  mobile?: string;
  profile_pic?: string;
}

interface CartInfoDefaults {
  cartcount: number;
  cartquantitycount: number;
  products: any[];
  cartprice: number;
  subtotal: number;
  mrptotal: number;
  shipping: number;
  ordertotal: number;
  total: number;
  shippingrate: string;
}

interface UserContextType {
  user: User | null;
  isLoggedIn: boolean;
  login: (userData: User) => void;
  logout: () => void;
  cartinfoData: CartInfoDefaults | null;
  setCartInfoData: (cartinfData: CartInfoDefaults) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [cartinfoData, setcartingInfoData] = useState<CartInfoDefaults | null>(null);

  // Check for user data in localStorage on component mount (persistent login)
  useEffect(() => {
    const storedUser = localStorage.getItem(SK.NEXTUSERINFODATA);
    if (storedUser) {
      if (isValidJSON(storedUser)) setUser(JSON.parse(storedUser)); // Rehydrate user from localStorage
    }
  }, []);

  useEffect(() => {
    const storedCartinfo = localStorage.getItem(SK.NEXTCARTINFODATA);
    if (storedCartinfo) {
      if (isValidJSON(storedCartinfo))
        setcartingInfoData(JSON.parse(storedCartinfo)); // Rehydrate user from localStorage
    }
  }, []);

  // Login function to merge new user data with existing state
  const login = (userData: User) => {
    setUser((prevUser) => {
      const updatedUser = {
        ...prevUser, // Preserve existing fields
        ...userData, // Overwrite with new fields
        username: userData.username || prevUser?.username || 'Guest', // Ensure default
        profile_pic: userData.profile_pic || prevUser?.profile_pic || '', // Ensure default
      };
      localStorage.setItem(SK.NEXTUSERINFODATA, JSON.stringify(updatedUser));
      return updatedUser;
    });
  };

  // Logout function
  const logout = () => {
    setUser(null);
    localStorage.removeItem(SK.NEXTUSERINFODATA);
  };

  // Set cart info data (unchanged)
  const setCartInfoData = (value: CartInfoDefaults) => {
    console.log('...calling setCartInfoData');
    const updatedCartInfoData = cartinfoData ? { ...cartinfoData, ...value } : value;
    setCartInfoData(updatedCartInfoData);
    localStorage.setItem(SK.NEXTCARTINFODATA, JSON.stringify(updatedCartInfoData));
  };

  return (
    <UserContext.Provider
      value={{
        user,
        isLoggedIn: !!user,
        login,
        logout,
        setCartInfoData,
        cartinfoData,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};